import React, { useState, useEffect } from "react";
import {
  UserPlus,
  Send,
  CheckCircle,
  Phone,
  UserCheck,
  Percent,
  ShoppingCart,
  Calendar,
  AlertCircle,
} from "lucide-react";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import LoadingSpinner from "../../components/LoadingSpinner";
import ErrorMessage from "../../components/ErrorMessage";
import { useLanguage } from "../../contexts/LanguageContext";
import type { AnalyticsFilters } from "../../types/analytics";
import { fetchAnalyticsOverview } from "./analyticsApi";
import type { AnalyticsOverviewResponse } from "../../types/analytics";
type AnalyticsTabId = "overview" | "funnel" | "operators" | "followups" | "tasks" | "quality";

interface OverviewTabProps {
  filters: AnalyticsFilters;
  onNavigateToTab: (tab: string) => void;
}

const CARD_DEFS: Array<{
  key: keyof AnalyticsOverviewResponse["cards"];
  icon: React.ElementType;
  labelKey: string;
  navigateTo?: AnalyticsTabId;
}> = [
  { key: "leadsCreated", icon: UserPlus, labelKey: "analytics.leadsCreated" },
  { key: "leadsSentToCC", icon: Send, labelKey: "analytics.leadsSentToCC" },
  { key: "leadsCompleted", icon: CheckCircle, labelKey: "analytics.leadsCompleted" },
  { key: "callsEnded", icon: Phone, labelKey: "analytics.callsEnded" },
  { key: "reachedCount", icon: UserCheck, labelKey: "analytics.reachedCount" },
  { key: "reachRate", icon: Percent, labelKey: "analytics.reachRate" },
  { key: "ordersCreated", icon: ShoppingCart, labelKey: "analytics.ordersCreated" },
  { key: "conversionRate", icon: Percent, labelKey: "analytics.conversionRate" },
  { key: "followUpsScheduled", icon: Calendar, labelKey: "analytics.followUpsScheduled" },
  { key: "followUpsOverdue", icon: AlertCircle, labelKey: "analytics.followUpsOverdue", navigateTo: "followups" },
];

const OverviewTab: React.FC<OverviewTabProps> = ({ filters, onNavigateToTab }) => {
  const { t } = useLanguage();
  const [data, setData] = useState<AnalyticsOverviewResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    fetchAnalyticsOverview(filters)
      .then((res) => {
        if (!cancelled) setData(res);
      })
      .catch((err) => {
        if (!cancelled) setError(err.message || t("analytics.error"));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, [filters.from, filters.to, filters.task, filters.operatorId, filters.outcome, t]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <LoadingSpinner />
      </div>
    );
  }
  if (error) {
    return <ErrorMessage message={error} onRetry={() => setError(null)} />;
  }
  if (!data) {
    return <p className="text-gray-500 text-center py-8">{t("analytics.noData")}</p>;
  }

  const { cards, series } = data;

  const formatRate = (v: number) => (v != null ? `${v}%` : "—");

  return (
    <div className="space-y-6">
      {/* Cards row */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4">
        {CARD_DEFS.map((def) => {
          const Icon = def.icon;
          const value = cards[def.key];
          const display =
            def.key === "reachRate" || def.key === "conversionRate"
              ? formatRate(value as number)
              : String(value ?? 0);
          const isClickable = !!def.navigateTo;
          return (
            <button
              key={def.key}
              type="button"
              onClick={() => def.navigateTo && onNavigateToTab(def.navigateTo!)}
              className={`bg-white rounded-lg shadow p-4 text-left flex items-center gap-3 transition-colors ${
                isClickable
                  ? "hover:bg-[#264a3e]/5 hover:ring-2 hover:ring-[#264a3e]/20 cursor-pointer"
                  : "cursor-default"
              }`}
            >
              <div className="flex-shrink-0">
                <Icon className="h-8 w-8 text-[#264a3e]" />
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium text-gray-500 truncate">{t(def.labelKey)}</p>
                <p className="text-xl font-semibold text-gray-900 truncate">{display}</p>
              </div>
            </button>
          );
        })}
      </div>

      {/* Charts row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-lg shadow p-4">
          <h3 className="text-sm font-medium text-gray-700 mb-4">{t("analytics.leadsCreated")}</h3>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={series.leadsCreatedDaily || []} margin={{ top: 5, right: 5, left: 0, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                <XAxis dataKey="date" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} />
                <Tooltip />
                <Line type="monotone" dataKey="value" stroke="#264a3e" strokeWidth={2} dot={{ r: 3 }} name={t("analytics.leadsCreated")} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
        <div className="bg-white rounded-lg shadow p-4">
          <h3 className="text-sm font-medium text-gray-700 mb-4">{t("analytics.callsEnded")}</h3>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={series.callsDaily || []} margin={{ top: 5, right: 5, left: 0, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                <XAxis dataKey="date" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} />
                <Tooltip />
                <Line type="monotone" dataKey="value" stroke="#264a3e" strokeWidth={2} dot={{ r: 3 }} name={t("analytics.callsEnded")} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow p-4">
        <h3 className="text-sm font-medium text-gray-700 mb-4">{t("analytics.ordersCreated")}</h3>
        <div className="h-64">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={series.ordersCreatedDaily || []} margin={{ top: 5, right: 5, left: 0, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
              <XAxis dataKey="date" tick={{ fontSize: 11 }} />
              <YAxis tick={{ fontSize: 11 }} />
              <Tooltip />
              <Line type="monotone" dataKey="value" stroke="#264a3e" strokeWidth={2} dot={{ r: 3 }} name={t("analytics.ordersCreated")} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
};

export default OverviewTab;
